import React, { useState } from 'react';
import { Lock, Mail, ArrowLeft } from 'lucide-react'; 
import logo from '../../assets/Dante-White-Full-01.png'; // Make sure to provide the path to your logo file
import { isSignedIn, resetPasswordEmail, signIn } from '../../firebase';
import { SignInView } from './SignInView';
import { ForgotPasswordView } from './ForgotPasswordView';
import {confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../firebase';


// --- MAIN COMPONENT ---

export const SignInPage = ({setUser, reset}) => {

  const {reset: passwordReset, code} = reset;

  // VIEW STATE: 'signIn' or 'forgotPassword'
  const [currentView, setCurrentView] = useState(passwordReset ? 'forgotPassword' : 'signIn'); 
  
  // State for controlled inputs
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Error and feedback states
  const [generalError, setGeneralError] = useState(null); 
  const [passwordError, setPasswordError] = useState(null); 
  const [isShaking, setIsShaking] = useState(false); 
  const [resetMessage, setResetMessage] = useState(null); // New state for reset feedback
      

  // Array of possible sign-in error messages
  const errorMessages = [
    "The Doors of Dante. Speak, friend, and enter.",
    "You do not know the word. You cannot find it.",
    "You cannot go forward. You must speak first with the Lord of the Wood.",
    "Stand by the grey stone when the thrush knocks.",
    "The way is shut, it was made by those who know the password.",
    "You shall not pass, without the secret word."
  ];

  // Helper to clear all errors at once
  const clearErrors = () => {
    setGeneralError(null);
    setPasswordError(null);
    setResetMessage(null); // Clear reset message too
    setIsShaking(false);
  };

  // Handlers for controlled inputs that also clear errors on type
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    clearErrors();
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    clearErrors();
  };

  // Handler for the password reset submission
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    clearErrors();

    if (passwordReset) {
         confirmPasswordReset(auth,code,password).then(() => {
        setResetMessage({type: "success", text: "Password reset! Redirecting to sign in 5 seconds..."});
        setTimeout(() => setCurrentView("signIn"), 5000);
      }).catch(({code}) => {
      if (code === "auth/invalid-action-code") {
         setResetMessage({type: "error", text: "This password reset link has expired. Please request a new one."});
         setIsShaking(true);
      }
      })
      return;
      }

    if (!email) {
      setResetMessage({
        type: 'error',
        text: 'Please enter your email address first.',
      });
      return;
    }

    try {

      setResetMessage({
        type: 'success',
        text: `Success! A password reset link has been sent to ${email}. Check your inbox.`,
      });
      resetPasswordEmail(email);
    } catch (err) {
      console.error("Password reset failed:", err); 
      setResetMessage({
        type: 'error',
        text: "Error: Could not find this account or send the email. Please check your address.",
      });
    }
  };

  // Handler for the sign-in submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    clearErrors(); // Clear previous errors

    try {
      // Attempt to sign in using state values
      const {user} = (await signIn(email, password))
      isSignedIn(true);
      setUser(user);
    } catch (err) {
      // If signIn fails, catch it
      console.error("Sign-in failed:", err); 
      
      // 1. Set the random general error for the top box
      const randomIndex = Math.floor(Math.random() * errorMessages.length);
      setGeneralError(errorMessages[randomIndex]); 
      
      // 2. Set the specific password error for under the input
      setPasswordError("Please enter correct password");
      
      // 3. Trigger the shake/glow animation
      setIsShaking(true);

      // 4. Clear password field for security/UX
      setPassword('');
    }
  };

  // --- MAIN RENDER ---
  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-slate-950 font-sans text-slate-100">
      {/* Background radial gradient for a high-tech feel */}
      <div className="absolute inset-0 z-0 opacity-20 bg-radial-gradient"></div>

      <div className="w-full max-w-md z-10">
        
        {/* Logo and New Text Container */}
        <a 
          href="https://partners.knoesis.co.uk"
          target="_blank" 
          rel="noopener noreferrer"
          className="flex flex-col items-center mb-10 group"
        >
          <img 
            src={logo} 
            alt="Dante Logo" 
            className="h-16 max-w-full object-contain animate-fade-in 
                       transition duration-300 ease-in-out cursor-pointer 
                       group-hover:drop-shadow-[0_0_10px_rgba(203,213,225,0.6)]"
          />
          <p className="mt-2 text-sm text-slate-400 font-space-grotesk tracking-widest uppercase">
            by Knoesis
          </p>
        </a>

        {/* Main sign-in card with glow effect */}
        {/* 🌟 CHANGE: Subtle glow on the card itself */}
        <div className={`bg-slate-900 p-10 rounded-2xl shadow-2xl border border-slate-800 backdrop-blur-sm animate-fade-in-up transition-all duration-500 ease-in-out hover:drop-shadow-[0_0_5px_rgba(203,213,225,0.4)]`}>
          
          {/* Outer wrapper to smoothly transition height */}
          <div className="transition-all duration-500 ease-in-out">
            {currentView === 'signIn' ? (
              <SignInView
                email={email}
                password={password}
                generalError={generalError}
                passwordError={passwordError}
                isShaking={isShaking}
                handleEmailChange={handleEmailChange}
                handlePasswordChange={handlePasswordChange}
                handleSubmit={handleSubmit}
                setCurrentView={setCurrentView}
                clearErrors={clearErrors}
                setUser={setUser}
              />
            ) : (
              <ForgotPasswordView
                email={email}
                handleEmailChange={handleEmailChange}
                handlePasswordChange={handlePasswordChange}
                handlePasswordReset={handlePasswordReset}
                resetMessage={resetMessage}
                setCurrentView={setCurrentView}
                clearErrors={clearErrors}
                resetTrigger={reset ? reset : undefined}
              />
            )}
          </div>

        </div>
        
        {/* Footer link/info */}
        <p className="text-center mt-6 text-sm text-slate-600">
          Not a member? <a href="https://knoesis.co.uk/dante-request-access" target="_blank" rel="noopener noreferrer" className="font-medium text-slate-100 hover:text-slate-300">Request Access</a>
        </p>

      </div>
    </div>
  );
};

export default SignInPage